import * as React from "react";
import { useState, useCallback } from "react";
import styled from "styled-components";
import TeamsQueryHelp from "security/components/teams-overview/TeamsQueryHelper";
import TeamsDomain from "security/domainActions/TeamsDomain";
import LicenseCheck from "license/LicenseCheck";
import PaginatedTeamItem from "security/components/teams-assignment/PaginatedTeamItem";
import useRoles from "common/hooks/useRoles";
import PaginatedItemOverview, { DEFAULT_PAGINATION } from "components/common/PaginatedItemOverview";
import { Table } from "components/bootstrap";
import { Spinner } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import TeamQuickCreate from "./TeamQuickCreate";
import TeamsSelector from "./TeamsSelector";
const TeamSelectionWrapper = (shouldShowActions) => ({ isEmptyResult, children }) => isEmptyResult ? /* @__PURE__ */ React.createElement("div", null, children) : /* @__PURE__ */ React.createElement(Table, { condensed: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "Name"), /* @__PURE__ */ React.createElement("th", null, "Description"), /* @__PURE__ */ React.createElement("th", null, "Roles"), shouldShowActions && /* @__PURE__ */ React.createElement("th", null, "Actions"))), children);
const TeamRenderer = (roles) => ({ item, onDeleteItem }) => {
  if (roles.isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const team = item;
  const assignedTeamRoles = roles.roles.filter((r) => team.roles.includes(r.id));
  const teamCanBeDeleted = !team.isSynced;
  return /* @__PURE__ */ React.createElement(
    PaginatedTeamItem,
    {
      roles: assignedTeamRoles,
      team,
      onDeleteItem: teamCanBeDeleted ? onDeleteItem : void 0
    }
  );
};
const Container = styled.div`
  margin-top: 15px;
  margin-bottom: 15px;
`;
const TeamsAssignment = ({
  readOnly = false,
  loadAssignedTeams,
  buildTeamWithEntity,
  getUnassignedTeams,
  unassignTeam,
  assignTeams
}) => {
  const [paginatedTeams, setPaginatedTeams] = useState();
  const [showCreationForm, setShowCreationForm] = useState(false);
  const roles = useRoles();
  const sendTelemetry = useSendTelemetry("teams-role");
  const _onLoad = useCallback((pagination = DEFAULT_PAGINATION) => loadAssignedTeams(pagination), [loadAssignedTeams]);
  const _unassignTeam = (team) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.TEAM.UNASSIGNED, {
      app_action_value: "unassign"
    });
    unassignTeam(team.id).then(() => {
      _onLoad().then(setPaginatedTeams);
    });
  };
  const _onAssignTeams = async (teamIds) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.TEAM.ASSIGNED, {
      app_action_value: "assign"
    });
    assignTeams(teamIds).then(() => {
      _onLoad().then(setPaginatedTeams);
    });
  };
  const _onCreateTeam = (newTeam) => TeamsDomain.create(buildTeamWithEntity(newTeam)).then((createdTeam) => {
    _onLoad().then(setPaginatedTeams);
    return createdTeam;
  });
  const TeamRendererWithRoles = TeamRenderer(roles);
  return /* @__PURE__ */ React.createElement(LicenseCheck, { featureName: "teams" }, ({ licenseIsValid }) => {
    const isReadOnly = readOnly || !licenseIsValid;
    const TeamSelectionWrapperWithReadOnly = TeamSelectionWrapper(!isReadOnly);
    return /* @__PURE__ */ React.createElement(React.Fragment, null, !isReadOnly && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", null, "Assign Teams"), /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
      TeamsSelector,
      {
        onSubmit: _onAssignTeams,
        getUnassignedTeams,
        showCreationForm: () => setShowCreationForm(true)
      }
    ))), !isReadOnly && showCreationForm ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", null, "Quick Create Team"), /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(TeamQuickCreate, { onClose: () => setShowCreationForm(false), onSubmit: _onCreateTeam }))) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", null, "Selected Teams"), /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
      PaginatedItemOverview,
      {
        noDataText: "No selected teams have been found",
        overrideList: paginatedTeams,
        onLoad: _onLoad,
        queryHelper: /* @__PURE__ */ React.createElement(TeamsQueryHelp, null),
        resultsWrapperComponent: TeamSelectionWrapperWithReadOnly,
        overrideItemComponent: TeamRendererWithRoles,
        onDeleteItem: !isReadOnly ? _unassignTeam : void 0
      }
    ))));
  });
};
export default TeamsAssignment;
